import {
  SUBCARPETAS_DORE,
  documentosSugeridosDore,
  type ConfiguracionDeCarpetasDore,
  type ResultadoDeRevisionDeCarpetasDore,
  type RutaDeCarpetaDore,
} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {
  useEstadoDeCarpetasDore,
  useGuardarCarpetasDore,
  useModeloDeCarpetaDore,
  useRevisarCarpetasDore,
} from '../api/dore-carpetas';
import {tr} from '../i18n/idioma';
import {fechaHora, numero} from '../utilidades/formato';
import {ListaDeChequeo} from './lista-de-chequeo';

const mensajeDe = (e: unknown) => (e instanceof ErrorDeApi ? e.message : tr('No se pudo.', 'It failed.'));
const campo = 'mt-1 w-full rounded-md border border-linea px-3 py-2 text-sm text-tinta';
const boton = 'rounded-md border border-linea px-3 py-1.5 text-sm hover:bg-fondo disabled:opacity-50';

// Carpetas de doré (decision 0095, solo administradores): por ruta, es decir
// por las letras del codigo, donde estan sus exportaciones y como se llaman.
export function CarpetasDeDore() {
  const estado = useEstadoDeCarpetasDore();
  const guardar = useGuardarCarpetasDore();
  const modelo = useModeloDeCarpetaDore();
  const revisar = useRevisarCarpetasDore();
  const [config, setConfig] = useState<ConfiguracionDeCarpetasDore>({activa: false, desde: null, rutas: []});
  const [rutaModelo, setRutaModelo] = useState('');
  const [prefijoNuevo, setPrefijoNuevo] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [revision, setRevision] = useState<ResultadoDeRevisionDeCarpetasDore | null>(null);

  useEffect(() => {
    if (estado.data) setConfig(estado.data.configuracion);
  }, [estado.data]);

  const cambiarRuta = (i: number, cambio: Partial<RutaDeCarpetaDore>) =>
    setConfig(c => ({...c, rutas: c.rutas.map((r, j) => (j === i ? {...r, ...cambio} : r))}));

  const tomarModelo = async () => {
    setMensaje(null);
    try {
      const m = await modelo.mutateAsync(rutaModelo);
      setConfig(c => ({...c, rutas: [...c.rutas.filter(r => r.prefijo !== m.ruta.prefijo), m.ruta]}));
      setMensaje(
        tr(
          `Tomada de ${m.codigo}${m.omitidas.length ? `; no se tomaron: ${m.omitidas.join(', ')}` : ''}. Revísala y guarda.`,
          `Taken from ${m.codigo}${m.omitidas.length ? `; left out: ${m.omitidas.join(', ')}` : ''}. Check it and save.`,
        ),
      );
    } catch (e) {
      setMensaje(mensajeDe(e));
    }
  };

  const agregar = () => {
    const prefijo = prefijoNuevo.trim().toUpperCase();
    if (!prefijo || config.rutas.some(r => r.prefijo === prefijo)) return;
    setConfig(c => ({
      ...c,
      rutas: [
        ...c.rutas,
        {
          prefijo,
          carpetaMadre: '',
          nombre: '{codigo}',
          subcarpetas: [...SUBCARPETAS_DORE],
          documentos: documentosSugeridosDore(),
        },
      ],
    }));
    setPrefijoNuevo('');
  };

  const enviar = async () => {
    setMensaje(null);
    try {
      await guardar.mutateAsync(config);
      setMensaje(tr('Guardado.', 'Saved.'));
    } catch (e) {
      setMensaje(mensajeDe(e));
    }
  };

  const e = estado.data;
  return (
    <form
      aria-label={tr('Carpetas de doré', 'Doré folders')}
      className="space-y-4 rounded-lg border border-linea bg-white p-5 text-sm"
      onSubmit={ev => {
        ev.preventDefault();
        void enviar();
      }}
    >
      <div>
        <h2 className="text-lg font-medium">{tr('Carpetas de doré', 'Doré folders')}</h2>
        <p className="text-apagado">
          {tr(
            'Cada ruta son las letras del código de la exportación. La app vincula cada exportación con la carpeta que ya tiene y crea la de las nuevas hechas en la app. A una carpeta que ya existe solo le completa el nombre. Nunca borra nada.',
            'Each route is the letters of the export code. The app links each export to the folder it already has and creates the folder of new ones made in the app. It only completes the name of an existing folder. It never deletes anything.',
          )}
        </p>
        {e && (
          <p className="mt-1 text-apagado">
            {tr(
              `${numero(e.vinculadas)} carpetas vinculadas · ${numero(e.conError)} con error · última revisión ${e.ultimaRevision ? fechaHora(e.ultimaRevision) : '—'}`,
              `${numero(e.vinculadas)} linked folders · ${numero(e.conError)} with errors · last check ${e.ultimaRevision ? fechaHora(e.ultimaRevision) : '—'}`,
            )}
          </p>
        )}
      </div>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={config.activa}
          onChange={ev => setConfig(c => ({...c, activa: ev.target.checked}))}
          className="mt-1 accent-verde"
          aria-label={tr(
            'Vincular y crear las carpetas de doré solas',
            'Link and create the doré folders automatically',
          )}
        />
        <span>
          <span className="font-medium">
            {tr('Vincular y crear las carpetas de doré solas', 'Link and create the doré folders automatically')}
          </span>
          <span className="mt-0.5 block text-xs text-apagado">
            {tr(
              'Cada pocos minutos. Solo reciben carpeta sola las exportaciones creadas en la app desde la fecha de abajo; las demás se crean con el botón de su ficha.',
              'Every few minutes. Only exports created in the app from the date below get a folder automatically; the rest are created with the button on their page.',
            )}
          </span>
        </span>
      </label>
      <label className="block">
        <span className="font-medium">{tr('Desde', 'From')}</span>
        <input
          type="date"
          value={config.desde ?? ''}
          onChange={ev => setConfig(c => ({...c, desde: ev.target.value || null}))}
          className="mt-1 block w-48 rounded-md border border-linea px-3 py-2 text-sm text-tinta"
        />
      </label>

      <div className="rounded-md border border-linea p-4">
        <p className="font-medium">
          {tr('Tomar una ruta de una carpeta de muestra', 'Take a route from a sample folder')}
        </p>
        <p className="mt-0.5 text-xs text-apagado">
          {tr(
            'Pega la ruta de la carpeta de una exportación reciente de esa ruta. La app toma la carpeta madre y el nombre. Las subcarpetas son las aprobadas, más la de documentos del proveedor si la muestra la tiene.',
            'Paste the path of the folder of a recent export on that route. The app takes the parent folder and the name. Subfolders are the approved ones, plus the supplier documents one if the sample has it.',
          )}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            aria-label={tr('Ruta de la carpeta de muestra de doré', 'Doré sample folder path')}
            value={rutaModelo}
            onChange={ev => setRutaModelo(ev.target.value)}
            className={`${campo} mt-0 flex-1`}
          />
          <button
            type="button"
            className={boton}
            disabled={!rutaModelo.trim() || modelo.isPending || !e?.conectado}
            onClick={() => void tomarModelo()}
          >
            {modelo.isPending ? tr('Leyendo…', 'Reading…') : tr('Tomar', 'Take')}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            aria-label={tr('Letras de la ruta nueva', 'Letters of the new route')}
            placeholder={tr('Letras del código', 'Code letters')}
            value={prefijoNuevo}
            onChange={ev => setPrefijoNuevo(ev.target.value)}
            className="w-48 rounded-md border border-linea px-3 py-1.5 text-sm text-tinta"
          />
          <button type="button" className={boton} disabled={!prefijoNuevo.trim()} onClick={agregar}>
            {tr('+ Ruta a mano', '+ Route by hand')}
          </button>
        </div>
      </div>

      {config.rutas.map((r, i) => (
        <fieldset key={r.prefijo} className="space-y-2 rounded-md border border-linea p-4">
          <div className="flex items-center justify-between">
            <legend className="font-medium">{tr(`Ruta ${r.prefijo}`, `Route ${r.prefijo}`)}</legend>
            <button
              type="button"
              className="text-alerta hover:underline"
              onClick={() => setConfig(c => ({...c, rutas: c.rutas.filter((_, j) => j !== i)}))}
            >
              {tr('Quitar', 'Remove')}
            </button>
          </div>
          <label className="block">
            {tr('Carpeta madre en Dropbox', 'Parent folder in Dropbox')}
            <input
              value={r.carpetaMadre}
              onChange={ev => cambiarRuta(i, {carpetaMadre: ev.target.value})}
              className={campo}
            />
          </label>
          <label className="block">
            {tr('Nombre de la carpeta', 'Folder name')}
            <input value={r.nombre} onChange={ev => cambiarRuta(i, {nombre: ev.target.value})} className={campo} />
          </label>
          <label className="block">
            {tr('Subcarpetas (una por renglón)', 'Subfolders (one per line)')}
            <textarea
              rows={Math.max(4, r.subcarpetas.length + 1)}
              value={r.subcarpetas.join('\n')}
              onChange={ev =>
                cambiarRuta(i, {
                  subcarpetas: ev.target.value
                    .split('\n')
                    .map(s => s.trim())
                    .filter(Boolean),
                })
              }
              className={`${campo} font-mono`}
            />
          </label>
          <ListaDeChequeo
            documentos={r.documentos}
            sugeridos={documentosSugeridosDore}
            cambiar={documentos => cambiarRuta(i, {documentos})}
          />
        </fieldset>
      ))}
      <p className="text-xs text-apagado">
        {tr(
          'Marcas del nombre: {codigo} = el código de la exportación; {venta} = la venta del proveedor. Sin venta, el nombre queda solo con el código.',
          'Name marks: {codigo} = the export code; {venta} = the supplier sale. Without a sale, the name is just the code.',
        )}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={guardar.isPending || estado.isPending}
          className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {guardar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar', 'Save')}
        </button>
        <button
          type="button"
          className={boton}
          disabled={revisar.isPending || !e?.conectado || !e.configuracion.activa}
          onClick={async () => {
            setMensaje(null);
            try {
              setRevision(await revisar.mutateAsync());
            } catch (err) {
              setMensaje(mensajeDe(err));
            }
          }}
        >
          {revisar.isPending ? tr('Revisando…', 'Checking…') : tr('↻ Revisar ahora', '↻ Check now')}
        </button>
        {mensaje && <span>{mensaje}</span>}
      </div>
      {revision && (
        <div>
          <p>
            {tr(
              `${numero(revision.revisadas)} revisadas, ${numero(revision.conCambios)} con cambios, ${numero(revision.conError)} con error.`,
              `${numero(revision.revisadas)} checked, ${numero(revision.conCambios)} changed, ${numero(revision.conError)} with errors.`,
            )}
          </p>
          {revision.sinExportacion.length > 0 && (
            <div className="mt-2">
              <p className="font-medium">
                {tr(
                  `Carpetas sin exportación en la app (${numero(revision.sinExportacion.length)}):`,
                  `Folders with no export in the app (${numero(revision.sinExportacion.length)}):`,
                )}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {revision.sinExportacion.map(s => (
                  <li key={s.ruta}>
                    <Link
                      to={`/dore/nueva?codigo=${encodeURIComponent(s.codigo)}`}
                      className="text-verde hover:underline"
                    >
                      {tr(`Crear ${s.codigo}`, `Create ${s.codigo}`)}
                    </Link>{' '}
                    <span className="break-all text-apagado">{s.ruta}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {revision.ambiguas.length > 0 && (
            <div className="mt-2">
              <p className="font-medium">
                {tr(
                  'Exportaciones con más de una carpeta posible (no se vincularon):',
                  'Exports with more than one possible folder (not linked):',
                )}
              </p>
              <ul className="mt-1 list-disc pl-5 text-apagado">
                {revision.ambiguas.map(a => (
                  <li key={a.codigo} className="break-all">
                    {a.codigo}: {a.rutas.join(' · ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul className="mt-2 list-disc pl-5 text-apagado">
            {revision.acciones.map(a => (
              <li key={a.codigo} className="break-all">
                {a.codigo}: {a.acciones.join(' · ')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
}
